import React, { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { useView } from '../../hooks/useView';
import { Sidebar } from './Sidebar';
import { TodayView } from '../views/TodayView';
import { InboxView, ArchiveView } from '../views/InboxView';
import { TodosView } from '../views/TodosView';
import { NotesView } from '../views/NotesView';
import { MemoryView } from '../views/MemoryView';
import { FocusPane } from '../focus/FocusPane';
import { OnboardingModal, isOnboardingComplete } from '../onboarding/OnboardingModal';
import { ComposeModal } from '../email/ComposeModal';
import { KeyboardShortcutsOverlay } from '../shared/KeyboardShortcutsOverlay';
import { OfflineBanner } from '../shared/OfflineBanner';
import { ToastContainer } from '../shared/ToastContainer';
import { CommandPalette } from '../command/CommandPalette';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useAppStore } from '../../stores/appStore';
import { useAuthStore } from '../../stores/authStore';
import { useEmailStore } from '../../stores/email-store';
import { Login, ForgotPasswordModal } from '../auth/Login';
import { Register } from '../auth/Register';
import { ConnectProvider } from '../auth/ConnectProvider';

type AuthScreen = 'login' | 'register' | 'connect-provider' | 'app';
type ComposeDraft = { to: string; cc: string; bcc: string; subject: string; body: string };

function parseComposeRecipients(value: string): Array<{ name?: string; email: string }> {
  return value
    .split(/[;,]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(.+?)\s*<([^>]+)>$/);
      if (match?.[2]) {
        const name = match[1]?.trim().replace(/^"|"$/g, '');
        return { ...(name ? { name } : {}), email: match[2].trim() };
      }
      return { email: part };
    });
}

// Lazy-load AskPanel - kept available for command palette / future entry points
// even though the sidebar layout no longer mounts a dedicated Ask view.
const AskPanel = lazy(() =>
  import('../ask/AskPanel').then((m) => ({ default: m.AskPanel }))
);
void AskPanel;

// App Shell

export function AppShell() {
  const [authScreen, setAuthScreen] = useState<AuthScreen>('login');
  const [checkingAuth, setCheckingAuth] = useState(true);
  const { view, setView } = useView();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState<boolean | null>(null);
  const [oauthNotification, setOauthNotification] =
    useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const initSyncAttemptedRef = useRef<Set<string>>(new Set());

  const { user, token, loadFromStorage, checkAuth, isLoading: authLoading, fetchConnections, connections } =
    useAuthStore();
  const triggerSync = useEmailStore((s) => s.triggerSync);
  const loadEmailConnections = useEmailStore((s) => s.loadConnections);
  const sendEmail = useEmailStore((s) => s.sendEmail);
  const isCommandPaletteOpen = useAppStore((s) => s.isCommandPaletteOpen);
  const setCommandPaletteOpen = useAppStore((s) => s.setCommandPaletteOpen);

  // OAuth callback handling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('oauth_success');
    const error = params.get('oauth_error');
    if (success) {
      const provider = success === 'gmail' ? 'Gmail' : 'Microsoft 365';
      setOauthNotification({ type: 'success', message: `${provider} connected!` });
      window.history.replaceState({}, '', '/');
      fetchConnections();
      void loadEmailConnections();
    }
    if (error) {
      const msg =
        error === 'access_denied'
          ? 'Connection was cancelled'
          : error === 'token_exchange_failed'
          ? 'Failed to connect Gmail. Please try again.'
          : error === 'invalid_state'
          ? 'Security check failed. Please try again.'
          : 'Failed to connect. Please try again.';
      setOauthNotification({ type: 'error', message: msg });
      window.history.replaceState({}, '', '/');
    }
  }, [fetchConnections, loadEmailConnections]);

  useEffect(() => {
    if (!oauthNotification) return;
    const t = setTimeout(() => setOauthNotification(null), 5000);
    return () => clearTimeout(t);
  }, [oauthNotification]);

  // Auth verification on mount
  useEffect(() => {
    const verify = async () => {
      try {
        await loadFromStorage();
        const isAuth = await checkAuth();
        if (isAuth) {
          await fetchConnections();
          await loadEmailConnections();
          setAuthScreen('app');
        } else {
          setAuthScreen('login');
        }
      } catch {
        setAuthScreen('login');
      } finally {
        setCheckingAuth(false);
      }
    };
    void verify();
  }, [loadFromStorage, checkAuth, fetchConnections, loadEmailConnections]);

  useEffect(() => {
    if (!checkingAuth) {
      if (user && token) {
        const hasConnections = connections.length > 0;
        if (hasConnections) {
          setAuthScreen('app');
          setShowOnboarding((prev) => (prev === null ? !isOnboardingComplete() : prev));
        } else {
          setAuthScreen('connect-provider');
        }
      } else {
        setAuthScreen('login');
      }
    }
  }, [user, token, checkingAuth, connections]);

  useEffect(() => {
    if (!user || !token || connections.length === 0) return;

    for (const conn of connections) {
      if (!conn.isActive || conn.lastSyncAt) continue;

      const key = `${user.id}:${conn.provider}`;
      if (initSyncAttemptedRef.current.has(key)) {
        continue;
      }

      initSyncAttemptedRef.current.add(key);
      void triggerSync(conn.provider, 'historical', 30).catch(() => {
        initSyncAttemptedRef.current.delete(key);
      });
    }
  }, [user, token, connections, triggerSync]);

  const handleOpenCommandPalette = useCallback(
    () => setCommandPaletteOpen(true),
    [setCommandPaletteOpen]
  );

  const handleCloseModal = useCallback(() => {
    if (composeOpen) {
      setComposeOpen(false);
      return;
    }
    if (shortcutsOpen) {
      setShortcutsOpen(false);
      return;
    }
    if (isCommandPaletteOpen) {
      setCommandPaletteOpen(false);
      return;
    }
  }, [composeOpen, shortcutsOpen, isCommandPaletteOpen, setCommandPaletteOpen]);

  const handleOpenCompose = useCallback(() => setComposeOpen(true), []);

  const handleSendCompose = useCallback(async (draft: ComposeDraft) => {
    const to = parseComposeRecipients(draft.to);
    if (to.length === 0 || !draft.body.trim()) {
      throw new Error('Recipient and message body are required.');
    }
    await sendEmail({
      to,
      cc: parseComposeRecipients(draft.cc),
      bcc: parseComposeRecipients(draft.bcc),
      subject: draft.subject.trim(),
      bodyText: draft.body,
    });
  }, [sendEmail]);

  const handleOpenNewNote = useCallback(() => {
    setView('notes');
    // Let the view mount, then ask it to open the editor.
    setTimeout(
      () => window.dispatchEvent(new Event('signa:open-note-editor')),
      0
    );
  }, [setView]);

  const handleFocusQuickAdd = useCallback(() => {
    setView('todos');
    setTimeout(
      () => window.dispatchEvent(new Event('signa:focus-todo-quickadd')),
      0
    );
  }, [setView]);

  useKeyboardShortcuts({
    onExpandPanel: () => {},
    onCollapseAll: () => {},
    onOpenCommandPalette: handleOpenCommandPalette,
    onOpenShortcuts: () => setShortcutsOpen(true),
    onCloseModal: handleCloseModal,
    onEmailNav: () => {},
    onEmailOpen: () => {},
    onEmailArchive: () => {},
    onEmailSnooze: () => {},
    onEmailReply: () => {},
    onEmailReplyAll: () => {},
    onEmailForward: () => {},
    onEmailToggleStar: () => {},
    onEmailDelete: () => {},
    onEmailToggleRead: () => {},
    onTodoNav: () => {},
    onTodoToggle: () => {},
    onTodoPriority: () => {},
    onNotesNav: () => {},
    onNotesOpen: () => {},
    onOpenCompose: handleOpenCompose,
    onOpenNewNote: handleOpenNewNote,
    onFocusQuickAdd: handleFocusQuickAdd,
  });

  // Event bus for CommandPalette + inline compose triggers
  useEffect(() => {
    const setComposePrefill = useAppStore.getState().setComposePrefill;
    const handlers: Array<[string, (e: Event) => void]> = [
      [
        'signa:compose',
        (e) => {
          const detail = (e as CustomEvent<{ to: string; subject: string; body: string }>).detail;
          if (detail) setComposePrefill(detail);
          setComposeOpen(true);
        },
      ],
      ['signa:new-note', () => {}],
      ['signa:ask-ai', () => {}],
    ];
    handlers.forEach(([ev, h]) => window.addEventListener(ev, h));
    return () => handlers.forEach(([ev, h]) => window.removeEventListener(ev, h));
  }, []);

  // Loading / auth gates
  if (checkingAuth || authLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full"
        style={{ backgroundColor: 'var(--bg-base)' }}
      >
        <div className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
          Signa
        </div>
        <div className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (authScreen === 'login') {
    return (
      <div className="h-full" style={{ backgroundColor: 'var(--bg-base)' }}>
        <Login
          onSwitchToRegister={() => setAuthScreen('register')}
          onForgotPassword={() => setShowForgotPassword(true)}
        />
        {showForgotPassword && (
          <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
        )}
      </div>
    );
  }
  if (authScreen === 'register') {
    return (
      <div className="h-full" style={{ backgroundColor: 'var(--bg-base)' }}>
        <Register
          onSwitchToLogin={() => setAuthScreen('login')}
          onSuccess={() => setAuthScreen('connect-provider')}
        />
      </div>
    );
  }
  if (authScreen === 'connect-provider') {
    return (
      <div className="h-full" style={{ backgroundColor: 'var(--bg-base)' }}>
        {oauthNotification && <OAuthToast notification={oauthNotification} />}
        <ConnectProvider onSkip={() => setAuthScreen('app')} />
      </div>
    );
  }

  // Main app - Signa 2.0 layout
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        background: 'var(--bg-base)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-ui)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Sidebar view={view} onViewChange={setView} />

      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {oauthNotification && <OAuthToast notification={oauthNotification} />}
        <OfflineBanner />

        <div
          style={{
            flex: 1,
            display: 'flex',
            minHeight: 0,
            position: 'relative',
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {view === 'today' && <TodayView />}
            {view === 'inbox' && <InboxView />}
            {view === 'archive' && <ArchiveView />}
            {view === 'todos' && <TodosView />}
            {view === 'notes' && <NotesView />}
            {view === 'memory' && <MemoryView />}
          </div>
          <FocusPane />
        </div>
      </main>

      <ComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSend={handleSendCompose}
      />
      <KeyboardShortcutsOverlay
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
      <CommandPalette />
      {showOnboarding === true && (
        <OnboardingModal onComplete={() => setShowOnboarding(false)} />
      )}
      <ToastContainer />
    </div>
  );
}

// OAuth Toast (inline helper)

function OAuthToast({
  notification,
}: {
  notification: { type: 'success' | 'error'; message: string };
}) {
  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-lg max-w-sm"
      style={{
        backgroundColor: 'var(--bg-surface)',
        border: `1px solid ${
          notification.type === 'success' ? 'var(--success)' : 'var(--error)'
        }`,
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      {notification.type === 'success' ? (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="var(--success)">
          <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm4.03 5.97l-5 5a.75.75 0 01-1.06 0l-2-2a.75.75 0 111.06-1.06L6.5 9.44l4.47-4.47a.75.75 0 111.06 1.06z" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="var(--error)">
          <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm3.5 9.5a.75.75 0 01-1.06 0L8 7.06 5.56 9.5a.75.75 0 01-1.06-1.06L6.94 6 4.5 3.56a.75.75 0 011.06-1.06L8 4.94l2.44-2.44a.75.75 0 011.06 1.06L9.06 6l2.44 2.44a.75.75 0 010 1.06z" />
        </svg>
      )}
      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
        {notification.message}
      </span>
    </div>
  );
}
